import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/hooks/useAuth';
import { extractApiError } from '@/lib/api';
import { friendlyError } from '@/lib/error-messages';
import { deleteMyAccount, exportMyData } from '@/lib/me';
import { Download, Loader2 } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const PLAN_LABEL: Record<string, string> = {
  free: 'Free',
  pro: 'Pro',
  team: 'Team',
  enterprise: 'Enterprise',
};

const ROLE_LABEL: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
  viewer: 'Viewer',
};

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(2)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

const DELETE_CONFIRM_PHRASE = 'DELETE';

export default function Settings() {
  const { user, org, quota, storage, memberships, subscription, isLoading } = useAuth();

  const [exporting, setExporting] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [deleteConfirm, setDeleteConfirm] = React.useState('');
  const [deleting, setDeleting] = React.useState(false);

  const role = React.useMemo(() => {
    const m = memberships.find((mm) => mm.org_id === org?.id) ?? memberships[0];
    return m ? (ROLE_LABEL[m.role] ?? m.role) : null;
  }, [memberships, org?.id]);

  async function handleExport() {
    setExporting(true);
    try {
      const result = await exportMyData();
      toast.success(
        result.inline
          ? 'Your data export has been downloaded.'
          : 'Export started — we\'ll email you a download link when it\'s ready.',
      );
    } catch (err) {
      const apiErr = extractApiError(err);
      toast.error(friendlyError(apiErr?.code, apiErr?.message ?? 'Could not export your data.'));
    } finally {
      setExporting(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      await deleteMyAccount();
      setDeleteOpen(false);
      toast.success('Erasure requested.');
    } catch (err) {
      const apiErr = extractApiError(err);
      toast.error(friendlyError(apiErr?.code, apiErr?.message ?? 'Could not delete your account.'));
    } finally {
      setDeleting(false);
    }
  }

  const planName = subscription?.plan ?? org?.plan ?? 'free';
  const planLabel = PLAN_LABEL[planName] ?? planName;

  const periodPct =
    quota && quota.period_cap && quota.period_cap > 0
      ? Math.round((quota.used_this_period / quota.period_cap) * 100)
      : null;

  const storagePct =
    storage && storage.bytes_limit && storage.bytes_limit > 0
      ? Math.round((storage.bytes_used / storage.bytes_limit) * 100)
      : null;

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground">
          Your profile, organization, plan and privacy controls.
        </p>
      </div>

      {/* Profile */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Profile</CardTitle>
          <CardDescription>Your account details.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <Skeleton className="h-20 w-full" />
          ) : (
            <>
              <div className="grid gap-1">
                <span className="text-xs font-medium text-muted-foreground">Email</span>
                <span className="text-sm">{user?.email ?? '—'}</span>
              </div>
              {user?.display_name ? (
                <div className="grid gap-1">
                  <span className="text-xs font-medium text-muted-foreground">Name</span>
                  <span className="text-sm">{user.display_name}</span>
                </div>
              ) : null}
              <Separator />
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm text-muted-foreground">
                  Change your password via the secure reset flow.
                </span>
                <Button asChild variant="outline" size="sm">
                  <Link to="/forgot-password">Change password</Link>
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Organization */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Organization</CardTitle>
          <CardDescription>The workspace your data belongs to.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <Skeleton className="h-16 w-full" />
          ) : (
            <>
              <div className="grid gap-1">
                <span className="text-xs font-medium text-muted-foreground">Name</span>
                <span className="text-sm">{org?.name ?? '—'}</span>
              </div>
              {role ? (
                <div className="grid gap-1">
                  <span className="text-xs font-medium text-muted-foreground">Your role</span>
                  <span className="text-sm">{role}</span>
                </div>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>

      {/* Plan & usage */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Plan &amp; usage</CardTitle>
          <CardDescription>
            Manage your plan on the{' '}
            <Link to="/billing" className="underline">
              billing page
            </Link>
            .
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground">Plan</span>
                <Badge variant={planName === 'free' ? 'secondary' : 'default'}>{planLabel}</Badge>
                {subscription && subscription.cancel_at_period_end ? (
                  <span className="text-xs text-muted-foreground">
                    Cancels on {new Date(subscription.current_period_end).toLocaleDateString()}
                  </span>
                ) : null}
              </div>

              {quota ? (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Analyses this period</span>
                    <span className="tabular-nums">
                      {quota.used_this_period.toLocaleString()} /{' '}
                      {quota.period_cap === null
                        ? 'Unlimited'
                        : quota.period_cap.toLocaleString()}
                    </span>
                  </div>
                  {periodPct !== null ? <Progress value={periodPct} /> : null}
                </div>
              ) : null}

              {storage ? (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Storage</span>
                    <span className="tabular-nums">
                      {fmtBytes(storage.bytes_used)} /{' '}
                      {storage.bytes_limit === null
                        ? 'Unlimited'
                        : fmtBytes(storage.bytes_limit)}
                    </span>
                  </div>
                  {storagePct !== null ? <Progress value={storagePct} /> : null}
                </div>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>

      {/* Data & privacy */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Data &amp; privacy</CardTitle>
          <CardDescription>Export or permanently erase your data (GDPR).</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="space-y-0.5">
              <p className="text-sm font-medium">Export my data</p>
              <p className="text-sm text-muted-foreground">
                Download a copy of your account, datasets and discoveries (Article 15).
              </p>
            </div>
            <Button variant="outline" onClick={handleExport} disabled={exporting}>
              {exporting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {exporting ? 'Exporting…' : 'Export my data'}
            </Button>
          </div>

          <Separator />

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="space-y-0.5">
              <p className="text-sm font-medium text-destructive">Delete account</p>
              <p className="text-sm text-muted-foreground">
                Permanently erase your account and all personal data (Article 17). This cannot be
                undone.
              </p>
            </div>
            <Button
              variant="destructive"
              onClick={() => {
                setDeleteConfirm('');
                setDeleteOpen(true);
              }}
            >
              Delete account
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog
        open={deleteOpen}
        onOpenChange={(o) => {
          if (!o && !deleting) setDeleteOpen(false);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete your account?</DialogTitle>
            <DialogDescription>
              This requests permanent erasure of your account and all data in your personal
              organization. Your account becomes inaccessible immediately; data is removed from
              storage within 30 days, per GDPR Article 17. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="delete-confirm">
              Type <span className="font-mono font-semibold">{DELETE_CONFIRM_PHRASE}</span> to
              confirm
            </Label>
            <Input
              id="delete-confirm"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              disabled={deleting}
              autoComplete="off"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting || deleteConfirm !== DELETE_CONFIRM_PHRASE}
            >
              {deleting ? 'Requesting…' : 'Delete account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
